import * as React from "react";

type TInputValue = string | number;

interface CellPersistenceParams<T extends HTMLInputElement | HTMLTextAreaElement> {
  initialValue: TInputValue;
  /** Called once on blur, with the last change event, when the value differs from initialValue. */
  onChange: (event: React.ChangeEvent<T>) => void;
  /** Chained after the commit. */
  onBlur?: (event: React.FocusEvent<T>) => void;
  /** Chained after the hook's own Escape handling. */
  onKeyDown?: (event: React.KeyboardEvent<T>) => void;
}

interface UseCellPersistenceResult<T extends HTMLInputElement | HTMLTextAreaElement> {
  value: string;
  handleChange: (event: React.ChangeEvent<T>) => void;
  handleBlur: (event: React.FocusEvent<T>) => void;
  handleKeyDown: (event: React.KeyboardEvent<T>) => void;
  /** Programmatically overwrites the displayed value and discards the pending edit. */
  setValue: (newValue: TInputValue) => void;
}

/**
 * Cell editing state: edits stay local while typing, commit once on blur, and Escape
 * reverts to the initial value (a later blur then commits nothing).
 */
export function useCellPersistence<T extends HTMLInputElement | HTMLTextAreaElement>({
  initialValue,
  onChange,
  onBlur,
  onKeyDown,
}: CellPersistenceParams<T>): UseCellPersistenceResult<T> {
  const normalizedInitialValue = React.useMemo(() => String(initialValue), [initialValue]);
  const [value, internalSetValue] = React.useState<string>(normalizedInitialValue);
  const pendingChangeEventRef = React.useRef<React.ChangeEvent<T> | undefined>(undefined);
  const onChangeEffectEvent = React.useEffectEvent(onChange);
  const onBlurEffectEvent = React.useEffectEvent((event: React.FocusEvent<T>) => onBlur?.(event));
  const onKeyDownEffectEvent = React.useEffectEvent((event: React.KeyboardEvent<T>) => onKeyDown?.(event));

  React.useLayoutEffect(() => {
    pendingChangeEventRef.current = undefined;
    // oxlint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    internalSetValue(normalizedInitialValue);
  }, [normalizedInitialValue]);

  const handleChange = React.useCallback((event: React.ChangeEvent<T>) => {
    pendingChangeEventRef.current = event;
    internalSetValue(event.target.value);
  }, []);

  const handleBlur = React.useCallback(
    (event: React.FocusEvent<T>) => {
      const pendingEvent = pendingChangeEventRef.current;
      pendingChangeEventRef.current = undefined;
      if (pendingEvent !== undefined && event.target.value !== normalizedInitialValue) {
        onChangeEffectEvent(pendingEvent);
      }
      onBlurEffectEvent(event);
    },
    [normalizedInitialValue],
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<T>) => {
      if (event.key === "Escape") {
        pendingChangeEventRef.current = undefined;
        internalSetValue(normalizedInitialValue);
      }
      onKeyDownEffectEvent(event);
    },
    [normalizedInitialValue],
  );

  const setValue = React.useCallback((newValue: TInputValue) => {
    pendingChangeEventRef.current = undefined;
    internalSetValue(String(newValue));
  }, []);

  return { value, handleChange, handleBlur, handleKeyDown, setValue };
}
